import './MembershipMemberTable.css';

import type { MembershipMemberRow } from '../api/membership.api';

type SortKey = 'name' | 'points' | 'lastVisit' | 'expiry';
type SortDirection = 'asc' | 'desc';

type MembershipMemberTableProps = {
  rows: MembershipMemberRow[];
  loading?: boolean;
  error?: string;
  page: number;
  hasNext: boolean;
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSortChange: (key: SortKey) => void;
  onPrev: () => void;
  onNext: () => void;
  onSelectMember?: (row: MembershipMemberRow) => void;
};

const formatRelativeTime = (value: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startOfTarget = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.round((startOfToday - startOfTarget) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
};

const formatExpiry = (value: string): { label: string; tone: 'neutral' | 'warning' | 'danger' } => {
  if (!value) return { label: '—', tone: 'neutral' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { label: '—', tone: 'neutral' };
  const diffMs = date.getTime() - Date.now();
  const diffDays = Math.ceil(diffMs / 86400000);
  if (diffDays <= 7) return { label: `${Math.max(diffDays, 0)} days`, tone: 'danger' };
  if (diffDays <= 30) return { label: `${diffDays} days`, tone: 'warning' };
  return {
    label: date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
    tone: 'neutral',
  };
};

const getInitials = (value: string): string => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'M';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');
};

const sortIndicator = (sortKey: SortKey, activeKey: SortKey, direction: SortDirection): string =>
  sortKey === activeKey ? (direction === 'asc' ? ' ↑' : ' ↓') : '';

const openMember = (row: MembershipMemberRow, onSelectMember?: (row: MembershipMemberRow) => void) => {
  onSelectMember?.(row);
};

const MembershipMemberTable = ({
  rows, loading, error, page, hasNext, sortKey, sortDirection, onSortChange, onPrev, onNext, onSelectMember
}: MembershipMemberTableProps) => (
  <article className="appointments-v2-card">
    <div className="appointments-v2-card-header">
      <h3>Members</h3>
      <div className="membership-member-table__pagination">
        <span className="membership-member-table__page-label">Page {page + 1}</span>
        <button type="button" className="btn ghost small" onClick={onPrev} disabled={page <= 0}>Prev</button>
        <button type="button" className="btn ghost small" onClick={onNext} disabled={!hasNext}>Next</button>
      </div>
    </div>
    <div className="appointments-v2-card-body">
      {loading ? <p className="appointments-v2-empty-copy">Loading members...</p> : null}
      {!loading && error ? <p className="appointments-v2-empty-copy">{error}</p> : null}
      {!loading && !error && rows.length === 0 ? (
        <div className="membership-member-table__empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <circle cx="32" cy="24" r="10" stroke="currentColor" strokeWidth="1.5" opacity="0.15" />
            <path d="M16 52c0-8.837 7.163-16 16-16s16 7.163 16 16" stroke="currentColor" strokeWidth="1.5" opacity="0.15" />
          </svg>
          <p className="appointments-v2-empty-copy">No members found.</p>
        </div>
      ) : null}
      {!loading && !error && rows.length > 0 ? (
        <table className="appointments-v2-table membership-member-table">
          <thead>
            <tr>
              <th>
                <button type="button" className="btn ghost small" onClick={() => onSortChange('name')}>
                  Member{sortIndicator('name', sortKey, sortDirection)}
                </button>
              </th>
              <th>Tier</th>
              <th>
                <button type="button" className="btn ghost small" onClick={() => onSortChange('points')}>
                  Points{sortIndicator('points', sortKey, sortDirection)}
                </button>
              </th>
              <th>Visits</th>
              <th>Pack</th>
              <th>
                <button type="button" className="btn ghost small" onClick={() => onSortChange('lastVisit')}>
                  Last visit{sortIndicator('lastVisit', sortKey, sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="btn ghost small" onClick={() => onSortChange('expiry')}>
                  Expiry{sortIndicator('expiry', sortKey, sortDirection)}
                </button>
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const expiry = formatExpiry(row.expiresAt);
              return (
                <tr
                  key={row.membershipUuid}
                  className={onSelectMember ? 'membership-member-table__row membership-member-table__row--interactive' : 'membership-member-table__row'}
                  onClick={onSelectMember ? () => openMember(row, onSelectMember) : undefined}
                  onKeyDown={onSelectMember ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openMember(row, onSelectMember);
                    }
                  } : undefined}
                  tabIndex={onSelectMember ? 0 : undefined}
                  role={onSelectMember ? 'button' : undefined}
                >
                  <td data-label="Member">
                    <div className="membership-member-table__identity">
                      <span className="membership-member-table__avatar">{getInitials(row.userName || row.phone)}</span>
                      <div>
                        <strong>{row.userName || row.phone || '—'}</strong>
                        <span>{row.phone || row.email || 'No contact info'}</span>
                      </div>
                    </div>
                  </td>
                  <td data-label="Tier">
                    <span className="membership-member-table__tierBadge">
                      {row.tierLevel ? `Tier ${row.tierLevel}` : 'Tier'}
                      {row.membershipTypeName ? ` · ${row.membershipTypeName}` : ''}
                    </span>
                  </td>
                  <td data-label="Points">{Math.trunc(Number(row.pointsBalance || 0))}</td>
                  <td data-label="Visits">{Number(row.totalVisits ?? 0)}</td>
                  <td data-label="Pack">
                    {String(row.benefitType) === 'VISITS' && (row.includedVisits ?? 0) > 0
                      ? `${Number(row.visitsUsed ?? 0)}/${Number(row.includedVisits)}`
                      : '—'}
                  </td>
                  <td data-label="Last visit">
                    {row.lastVisitedAt ? formatRelativeTime(row.lastVisitedAt) : 'Never'}
                  </td>
                  <td data-label="Expiry">
                    <span className={`membership-member-table__expiry is-${expiry.tone}`}>
                      {expiry.label}
                    </span>
                  </td>
                  <td data-label="">
                    <div className="membership-member-table__actions">
                      <button
                        type="button"
                        className="btn ghost small"
                        onClick={(event) => {
                          event.stopPropagation();
                          openMember(row, onSelectMember);
                        }}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  </article>
);

export default MembershipMemberTable;
